<template>
  <a-card :bordered="false">
    <a-row :gutter="24">
      <a-col :span="24">
        <!-- 条件搜索 -->
        <div class="table-page-search-wrapper">
          <a-form layout="inline">
            <a-row :gutter="[16, 8]">
              <a-col :xxl="6" :xl="8" :lg="8" :md="8" :sm="24" :xs="8">
                <a-form-item label="分组" prop="configGroup">
                  <a-input
                    v-model:value="queryParam.configGroup"
                    placeholder="请输入分组"
                    allow-clear
                    class="no-border-radius-input"
                  />
                </a-form-item>
              </a-col>
              <a-col :xxl="6" :xl="8" :lg="8" :md="8" :sm="24" :xs="8">
                <a-form-item label="变量名" prop="configName">
                  <a-input
                    v-model:value="queryParam.configName"
                    placeholder="请输入变量名"
                    allow-clear
                    class="no-border-radius-input"
                  />
                </a-form-item>
              </a-col>
              <a-col :xxl="6" :xl="8" :lg="8" :md="8" :sm="24" :xs="8">
                <a-form-item label="变量标题" prop="title">
                  <a-input
                    v-model:value="queryParam.title"
                    placeholder="请输入变量标题"
                    allow-clear
                    class="no-border-radius-input"
                  />
                </a-form-item>
              </a-col>
              <a-col :xxl="6" :xl="8" :lg="8" :md="8" :sm="24" :xs="8">
                <span class="table-page-search-submitButtons">
                  <a-button type="primary" @click="handleQuery" class="search-button">查询</a-button>
                  <a-button style="margin-left: 8px" @click="resetQuery" class="reset-button">重置</a-button>
                </span>
              </a-col>
            </a-row>
          </a-form>
        </div>
        <!-- 操作 -->
        <div class="table-operations">
          <a-button type="text" shape="circle" @click="refreshData" class="refresh-icon-button">
            <img src="@/assets/images/projectUser/refresh.png" alt="刷新" />
          </a-button>
          <a-button type="primary" @click="handleAddClick" v-hasPermi="['config:config:add']" class="add-button">
            <PlusOutlined />新增
          </a-button>
          <a-button
            :disabled="single"
            @click="handleUpdateClick"
            v-hasPermi="['config:config:edit']"
            class="no-border-button"
          >
            <EditOutlined />修改
          </a-button>
          <a-button
            :disabled="multiple"
            @click="handleDelete"
            v-hasPermi="['config:config:remove']"
            class="no-border-button"
          >
            <DeleteOutlined />删除
          </a-button>
        </div>
        <!-- 增加修改 -->
        <CreateForm ref="createFormRef" @ok="getList" />
        <!-- 数据展示 -->
        <div style="margin-top: 20px">
          <a-table
            :loading="loading"
            :size="tableSize"
            rowKey="id"
            :columns="columns"
            :data-source="list"
            :row-selection="{
              selectedRowKeys: selectedRowKeys,
              onChange: onSelectChange,
            }"
            :pagination="false"
            :bordered="tableBordered"
            :scroll="{ x: 1600 }"
            class="optimized-table"
          >
            <template #configValue="{ record }">
              <div v-if="record.type == 4">
                <img style="width: 48px; height: 48px" v-image-preview :src="record.configValue" />
              </div>
              <a-tooltip v-else>
                <template #title>{{ record.configValue }}</template>
                <span class="config-value-text">{{ truncateText(record.configValue, 30) }}</span>
              </a-tooltip>
            </template>
            <template #createTime="{ record }">
              <span>
                {{ parseTime(record.createTime) }}
              </span>
            </template>
            <template #operation="{ record }">
              <div class="table-action-buttons">
                <a @click="() => handleEditClick(record)" v-hasPermi="['config:config:edit']" class="edit-link">
                  修改
                </a>
                <span class="action-divider" v-hasPermi="['config:config:remove']">|</span>
                <a @click="handleDelete(record)" v-hasPermi="['config:config:remove']" class="delete-link"> 删除 </a>
              </div>
            </template>
          </a-table>
        </div>
        <!-- 分页 -->
        <div class="pagination-wrapper">
          <div class="pagination-left">
            <span class="pagination-info">
              显示第 {{ (queryParam.pageNum - 1) * queryParam.pageSize + 1 }} 到第
              {{ Math.min(queryParam.pageNum * queryParam.pageSize, total) }} 条数据/共 {{ total }} 条
            </span>
            <a-select
              v-model:value="queryParam.pageSize"
              style="width: 100px; margin-left: 8px"
              @change="onShowSizeChange(queryParam.pageNum, queryParam.pageSize)"
              class="no-border-radius-select"
            >
              <a-select-option :value="10">10条/页</a-select-option>
              <a-select-option :value="20">20条/页</a-select-option>
              <a-select-option :value="50">50条/页</a-select-option>
              <a-select-option :value="100">100条/页</a-select-option>
            </a-select>
          </div>
          <div class="pagination-right">
            <a-pagination
              :current="queryParam.pageNum"
              :total="total"
              :page-size="queryParam.pageSize"
              :show-total="false"
              @change="changeSize"
            />
          </div>
        </div>
      </a-col>
    </a-row>
  </a-card>
</template>

<script setup>
import { ref, reactive, computed, onMounted, nextTick } from 'vue'
import { useStore } from 'vuex'
import { Modal, message } from 'ant-design-vue'
import { SearchOutlined, RedoOutlined, PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons-vue'
import { pageConfig, delConfig } from '@/api/app/config'
import CreateForm from './modules/CreateForm.vue'
import { parseTime } from '@/utils/ruoyi'
import { download } from '@/utils/request'

// Store
const store = useStore()

// 表格大小和边框（从 tableMixin 转换）
const tableSize1 = ref('')
const tableBordered = computed(() => store.state.app.tableBordered)
const tableSize = computed({
  get() {
    return tableSize1.value || store.state.app.tableSize
  },
  set(value) {
    tableSize1.value = value
  },
})

// 数据
const list = ref([])
const selectedRowKeys = ref([])
const selectedRows = ref([])
const advanced = ref(false)
const single = ref(true)
const multiple = ref(true)
const ids = ref([])
const loading = ref(false)
const total = ref(0)

// 查询参数
const queryParam = reactive({
  configGroup: null,
  configName: null,
  title: null,
  description: null,
  configValue: null,
  configValueEn: null,
  type: null,
  pageNum: 1,
  pageSize: 10,
})

// 表格列配置
const columns = [
  {
    title: '序号',
    dataIndex: 'index',
    align: 'center',
    width: 80,
    customRender: ({ index }) => {
      const { pageNum, pageSize } = queryParam
      return (pageNum - 1) * pageSize + index + 1
    },
  },
  {
    title: 'ID',
    dataIndex: 'id',
    ellipsis: true,
    align: 'center',
  },
  {
    title: '分组',
    dataIndex: 'configGroup',
    ellipsis: false,
    align: 'center',
    customCell: () => {
      return {
        class: 'config-group-cell',
      }
    },
  },
  {
    title: '变量名',
    dataIndex: 'configName',
    ellipsis: false,
    align: 'center',
    width: 180,
    customCell: () => {
      return {
        class: 'config-name-cell',
      }
    },
  },
  {
    title: '变量标题',
    dataIndex: 'title',
    ellipsis: false,
    align: 'center',
    width: 180,
    customCell: () => {
      return {
        class: 'config-title-cell',
      }
    },
  },
  {
    title: '描述',
    dataIndex: 'description',
    ellipsis: false,
    align: 'center',
    customCell: () => {
      return {
        class: 'config-description-cell',
      }
    },
  },
  {
    title: '值',
    dataIndex: 'configValue',
    ellipsis: true,
    align: 'center',
    width: 300,
    slots: { customRender: 'configValue' },
  },
  {
    title: '操作',
    dataIndex: 'operation',
    width: 200,
    fixed: 'right',
    slots: { customRender: 'operation' },
    align: 'center',
  },
]

// 组件引用
const createFormRef = ref(null)

// 计算属性
const submitButtonStyle = computed(() => {
  return advanced.value ? { float: 'right', overflow: 'hidden' } : {}
})

// 查询系统配置列表
const getList = () => {
  loading.value = true
  pageConfig(queryParam).then((response) => {
    list.value = response.data.records
    total.value = response.data.total
    loading.value = false
  })
}

// 搜索按钮操作
const handleQuery = () => {
  queryParam.pageNum = 1
  getList()
}

// 刷新按钮操作
const refreshData = () => {
  getList()
}

// 重置按钮操作
const resetQuery = () => {
  Object.assign(queryParam, {
    configGroup: undefined,
    configName: undefined,
    title: undefined,
    description: undefined,
    configValue: undefined,
    configValueEn: undefined,
    type: undefined,
    pageNum: 1,
    pageSize: 10,
  })
  handleQuery()
}

// 分页大小改变
const onShowSizeChange = (current, pageSize) => {
  queryParam.pageSize = pageSize
  getList()
}

// 分页改变
const changeSize = (current, pageSize) => {
  queryParam.pageNum = current
  queryParam.pageSize = pageSize
  getList()
}

// 选择改变
const onSelectChange = (selectedKeys, selectedRowsData) => {
  selectedRowKeys.value = selectedKeys
  selectedRows.value = selectedRowsData
  ids.value = selectedRowsData.map((item) => item.id)
  single.value = selectedKeys.length !== 1
  multiple.value = !selectedKeys.length
}

// 切换高级搜索
const toggleAdvanced = () => {
  advanced.value = !advanced.value
}

// 截取文本，超过指定长度显示省略号
const truncateText = (text, maxLength = 30) => {
  if (!text) return ''
  const textStr = String(text)
  if (textStr.length <= maxLength) return textStr
  return textStr.substring(0, maxLength) + '...'
}

// 新增按钮点击
const handleAddClick = async () => {
  await nextTick()
  console.log('handleAddClick 被调用')
  console.log('createFormRef.value:', createFormRef.value)
  if (createFormRef.value) {
    console.log('createFormRef.value 存在，方法列表:', Object.keys(createFormRef.value))
    if (typeof createFormRef.value.handleAdd === 'function') {
      console.log('调用 handleAdd')
      createFormRef.value.handleAdd()
    } else {
      console.error('handleAdd 方法不存在或不是函数')
      message.error('表单组件未正确初始化，请刷新页面重试')
    }
  } else {
    console.error('CreateForm 组件引用未找到')
    message.error('表单组件未找到，请刷新页面重试')
  }
}

// 修改按钮点击
const handleUpdateClick = () => {
  if (createFormRef.value && createFormRef.value.handleUpdate) {
    createFormRef.value.handleUpdate(undefined, ids.value)
  } else {
    console.error('CreateForm 组件引用未找到或 handleUpdate 方法不存在')
  }
}

// 编辑按钮点击（表格操作列）
const handleEditClick = (record) => {
  if (createFormRef.value && createFormRef.value.handleUpdate) {
    createFormRef.value.handleUpdate(record, undefined)
  } else {
    console.error('CreateForm 组件引用未找到或 handleUpdate 方法不存在')
  }
}

// 删除按钮操作
const handleDelete = (row) => {
  const tempIds = row?.id ? [...ids.value, row.id] : ids.value

  // 计算序号
  let indexText = ''
  if (row && row.id) {
    // 单行删除，找到该行在列表中的索引
    const index = list.value.findIndex((item) => item.id === row.id)
    if (index !== -1) {
      const serialNumber = (queryParam.pageNum - 1) * queryParam.pageSize + index + 1
      indexText = serialNumber.toString()
    }
  } else if (selectedRows.value && selectedRows.value.length > 0) {
    // 多行删除，计算所有选中行的序号
    const serialNumbers = selectedRows.value
      .map((item) => {
        const index = list.value.findIndex((listItem) => listItem.id === item.id)
        if (index !== -1) {
          return (queryParam.pageNum - 1) * queryParam.pageSize + index + 1
        }
        return null
      })
      .filter((num) => num !== null)
    indexText = serialNumbers.join('、')
  }

  Modal.confirm({
    title: '确认删除所选中数据?',
    content: indexText ? `删除表格序号为：${indexText}` : '确认删除所选数据?',
    onOk() {
      const data = tempIds.map((id) => ({ id: id }))
      return delConfig(data).then(() => {
        onSelectChange([], [])
        getList()
        message.success('删除成功', 3)
      })
    },
    onCancel() {
      // 取消清空删除ids列表
      ids.value = []
    },
  })
}

// 导出按钮操作
const handleExport = () => {
  Modal.confirm({
    title: '是否确认导出?',
    content: '此操作将导出当前条件下所有数据而非选中数据',
    onOk() {
      download(
        'app/app-config/export',
        {
          ...queryParam,
        },
        `系统配置_${new Date().getTime()}.xlsx`
      )
    },
    onCancel() {},
  })
}

// 生命周期
onMounted(() => {
  getList()
})
</script>

<style scoped>
/* 条件搜索区域样式 */
.table-page-search-wrapper :deep(.ant-form-item-label) {
  width: 80px;
  min-width: 80px;
}

.table-page-search-wrapper :deep(.ant-form-item-label > label) {
  width: 80px;
  text-align: right;
}

/* 移除所有输入框圆角的通用样式 */
.no-border-radius-input :deep(.ant-input) {
  border-radius: 0 !important;
}

.no-border-radius-input :deep(.ant-input-affix-wrapper) {
  border-radius: 0 !important;
}

.no-border-radius-picker :deep(.ant-picker) {
  border-radius: 0 !important;
}

.no-border-radius-select :deep(.ant-select-selector) {
  border-radius: 0 !important;
}

.no-border-radius-input-number :deep(.ant-input-number) {
  border-radius: 0 !important;
}

.no-border-radius-input-number :deep(.ant-input-number-input) {
  border-radius: 0 !important;
}

/* 搜索区域输入框样式覆盖 - 优先级高于 no-border-radius-input */
/* 普通输入框样式 */
.table-page-search-wrapper :deep(.no-border-radius-input .ant-input),
.table-page-search-wrapper :deep(.ant-form-item-control-input .ant-input) {
  background: #ffffff !important;
  border-radius: 4px !important;
  border: 1px solid #c7c7c7 !important;
}

/* 带前缀/后缀的输入框外层样式 */
.table-page-search-wrapper :deep(.no-border-radius-input .ant-input-affix-wrapper),
.table-page-search-wrapper :deep(.ant-input-affix-wrapper) {
  background: #ffffff !important;
  border-radius: 4px !important;
  border: 1px solid #c7c7c7 !important;
}

/* 带前缀/后缀的输入框内部input去掉边框，避免嵌套 */
.table-page-search-wrapper :deep(.ant-input-affix-wrapper .ant-input),
.table-page-search-wrapper :deep(.ant-input-affix-wrapper > .ant-input),
.table-page-search-wrapper :deep(.no-border-radius-input .ant-input-affix-wrapper .ant-input),
.table-page-search-wrapper :deep(.no-border-radius-input .ant-input-affix-wrapper > .ant-input),
.table-page-search-wrapper :deep(.ant-form-item .ant-input-affix-wrapper .ant-input),
.table-page-search-wrapper :deep(.ant-form-item-control-input-content .ant-input-affix-wrapper .ant-input),
.table-page-search-wrapper :deep(.ant-input-affix-wrapper .ant-input:hover),
.table-page-search-wrapper :deep(.ant-input-affix-wrapper .ant-input:focus),
.table-page-search-wrapper :deep(.ant-input-affix-wrapper .ant-input-focused),
.table-page-search-wrapper :deep(.ant-input-affix-wrapper-focused .ant-input),
.table-page-search-wrapper :deep(.ant-input-affix-wrapper-focused .ant-input:focus),
.table-page-search-wrapper :deep(.no-border-radius-input .ant-input-affix-wrapper .ant-input:hover),
.table-page-search-wrapper :deep(.no-border-radius-input .ant-input-affix-wrapper .ant-input:focus),
.table-page-search-wrapper :deep(.no-border-radius-input .ant-input-affix-wrapper .ant-input-focused),
.table-page-search-wrapper :deep(.ant-input-affix-wrapper input.ant-input),
.table-page-search-wrapper :deep(.ant-input-affix-wrapper .ant-input:not(:focus):not(:hover)),
.table-page-search-wrapper :deep(.ant-input-affix-wrapper .ant-input[type='text']) {
  border: 0 !important;
  border-top: 0 !important;
  border-right: 0 !important;
  border-bottom: 0 !important;
  border-left: 0 !important;
  border-width: 0 !important;
  border-style: none !important;
  border-color: transparent !important;
  background: transparent !important;
  background-color: transparent !important;
  box-shadow: none !important;
  outline: none !important;
  width: 100% !important;
  -webkit-appearance: none !important;
  -moz-appearance: none !important;
  appearance: none !important;
}

/* 日期选择器样式 */
.table-page-search-wrapper :deep(.no-border-radius-picker .ant-picker),
.table-page-search-wrapper :deep(.ant-picker) {
  background: #ffffff !important;
  border-radius: 4px !important;
  border: 1px solid #c7c7c7 !important;
}

/* 下拉选择框样式 */
.table-page-search-wrapper :deep(.no-border-radius-select .ant-select-selector),
.table-page-search-wrapper :deep(.ant-select-selector) {
  background: #ffffff !important;
  border-radius: 4px !important;
  border: 1px solid #c7c7c7 !important;
}

.table-page-search-wrapper :deep(.ant-input-number) {
  border-radius: 0 !important;
}

/* 搜索区域数字输入框样式 */
.table-page-search-wrapper :deep(.no-border-radius-input-number.ant-input-number),
.table-page-search-wrapper :deep(.no-border-radius-input-number) {
  border-radius: 4px !important;
  border: 1px solid #c7c7c7 !important;
}

/* 移除搜索栏输入框、下拉选择框、日期选择框聚焦时的高亮效果 */
/* 输入框聚焦样式 - 内部input隐藏边框 */
.table-page-search-wrapper :deep(.no-border-radius-input .ant-input:focus),
.table-page-search-wrapper :deep(.no-border-radius-input .ant-input-focused),
.table-page-search-wrapper :deep(.ant-input:focus),
.table-page-search-wrapper :deep(.ant-input-focused) {
  border: none !important;
  box-shadow: none !important;
  outline: none !important;
}

/* 带前缀/后缀的输入框聚焦样式 - 外部容器保持边框，内部input隐藏边框 */
.table-page-search-wrapper :deep(.no-border-radius-input .ant-input-affix-wrapper:focus),
.table-page-search-wrapper :deep(.no-border-radius-input .ant-input-affix-wrapper-focused),
.table-page-search-wrapper :deep(.ant-input-affix-wrapper:focus),
.table-page-search-wrapper :deep(.ant-input-affix-wrapper-focused) {
  border: 1px solid #c7c7c7 !important;
  box-shadow: none !important;
  outline: none !important;
}

/* 聚焦时，内部input隐藏边框 */
.table-page-search-wrapper :deep(.no-border-radius-input .ant-input-affix-wrapper:focus .ant-input),
.table-page-search-wrapper :deep(.no-border-radius-input .ant-input-affix-wrapper-focused .ant-input),
.table-page-search-wrapper :deep(.ant-input-affix-wrapper:focus .ant-input),
.table-page-search-wrapper :deep(.ant-input-affix-wrapper-focused .ant-input) {
  border: none !important;
  box-shadow: none !important;
  outline: none !important;
}

/* 下拉选择框聚焦样式 */
.table-page-search-wrapper :deep(.no-border-radius-select .ant-select-selector:focus),
.table-page-search-wrapper :deep(.no-border-radius-select .ant-select-focused .ant-select-selector),
.table-page-search-wrapper :deep(.ant-select-selector:focus),
.table-page-search-wrapper :deep(.ant-select-focused .ant-select-selector) {
  border: 1px solid #c7c7c7 !important;
  box-shadow: none !important;
  outline: none !important;
}

/* 日期选择框聚焦样式 */
.table-page-search-wrapper :deep(.no-border-radius-picker .ant-picker:focus),
.table-page-search-wrapper :deep(.no-border-radius-picker .ant-picker-focused),
.table-page-search-wrapper :deep(.ant-picker:focus),
.table-page-search-wrapper :deep(.ant-picker-focused) {
  border: 1px solid #c7c7c7 !important;
  box-shadow: none !important;
  outline: none !important;
}

/* 搜索按钮和新增按钮样式 */
.search-button,
.add-button {
  background-color: #165dff !important;
  border-color: #165dff !important;
  border-radius: 4px !important;
}

.search-button:hover,
.add-button:hover {
  background-color: #4080ff !important;
  border-color: #4080ff !important;
}

.search-button:focus,
.add-button:focus {
  background-color: #165dff !important;
  border-color: #165dff !important;
}

/* 其他按钮圆角设置 */
.table-page-search-wrapper :deep(.ant-btn) {
  border-radius: 4px;
}

.table-operations :deep(.ant-btn) {
  border-radius: 4px;
}

/* 全局按钮圆角设置 */
:deep(.ant-btn) {
  border-radius: 4px !important;
}

.table-action-buttons a {
  color: #1890ff;
  cursor: pointer;
  white-space: nowrap; /* 防止操作按钮换行 */
}

.table-action-buttons a:hover {
  color: #40a9ff;
}

/* 编辑按钮颜色 */
.table-action-buttons .edit-link {
  color: #165dff !important;
}

.table-action-buttons .edit-link:hover {
  color: #4080ff !important;
}

/* 删除按钮颜色 - 红色 */
.table-action-buttons .delete-link {
  color: #ff4d4f !important;
}

.table-action-buttons .delete-link:hover {
  color: #ff7875 !important;
}

.action-divider {
  margin: 0 8px;
  color: #d9d9d9;
  white-space: nowrap;
}

/* 去掉指定按钮的边框 */
.no-border-button {
  border: none !important;
  background-color: #f3f3f3 !important;
  color: #101010 !important;
}

.no-border-button:hover,
.no-border-button:focus {
  border: none !important;
  background-color: #f3f3f3 !important;
  color: #101010 !important;
}

.no-border-button[disabled] {
  background-color: #f5f5f5 !important;
  color: rgba(0, 0, 0, 0.25) !important;
  cursor: not-allowed !important;
}

.no-border-button[disabled]:hover,
.no-border-button[disabled]:focus {
  background-color: #f5f5f5 !important;
  color: rgba(0, 0, 0, 0.25) !important;
  cursor: not-allowed !important;
}

/* 重置按钮样式 */
.reset-button {
  background-color: #f3f3f3 !important;
  color: #101010 !important;
  border-color: #f3f3f3 !important;
  border-radius: 4px !important;
}

.reset-button:hover {
  background-color: #e6e6e6 !important;
  color: #101010 !important;
  border-color: #e6e6e6 !important;
}

.reset-button:focus {
  background-color: #f3f3f3 !important;
  color: #101010 !important;
  border-color: #f3f3f3 !important;
}

/* 刷新按钮样式 */
.refresh-icon-button {
  width: 32px;
  height: 32px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.refresh-icon-button img {
  width: 32px;
  height: 32px;
}

.table-page-search-wrapper {
  margin-bottom: 16px;
}

.table-operations {
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.pagination-wrapper {
  margin-top: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.pagination-left {
  display: flex;
  align-items: center;
}

.pagination-info {
  color: rgba(0, 0, 0, 0.65);
}

.pagination-right {
  display: flex;
  align-items: center;
  gap: 16px;
}

/* 分页组件样式 - 去掉圆角 */
.pagination-wrapper :deep(.ant-btn) {
  border-radius: 0 !important;
}

.pagination-wrapper :deep(.ant-pagination-item) {
  border-radius: 0 !important;
}

.pagination-wrapper :deep(.ant-pagination-prev),
.pagination-wrapper :deep(.ant-pagination-next),
.pagination-wrapper :deep(.ant-pagination-prev .ant-pagination-item-link),
.pagination-wrapper :deep(.ant-pagination-next .ant-pagination-item-link) {
  border-radius: 0 !important;
}

.pagination-wrapper :deep(.ant-pagination-prev button),
.pagination-wrapper :deep(.ant-pagination-next button) {
  border-radius: 0 !important;
}

.pagination-wrapper :deep(.ant-input-number) {
  border-radius: 0 !important;
}

.pagination-wrapper :deep(.ant-input-number-input) {
  border-radius: 0 !important;
}

/* 分页选中页数样式 */
.pagination-wrapper :deep(.ant-pagination-item-active) {
  background: #165dff !important;
  border-color: #165dff !important;
  border-radius: 4px !important;
}

.pagination-wrapper :deep(.ant-pagination-item-active a) {
  color: #ffffff !important;
}

.pagination-wrapper :deep(.ant-pagination-item-active:hover) {
  background: #165dff !important;
  border-color: #165dff !important;
}

.pagination-wrapper :deep(.ant-pagination-item-active:hover a) {
  color: #ffffff !important;
}

/* 优化表格样式 */
.optimized-table :deep(.ant-table-thead > tr > th) {
  white-space: nowrap; /* 表头不换行 */
  overflow: hidden;
  text-overflow: ellipsis;
  background-color: #fafafa;
  font-family: PingFang SC, PingFang SC;
  font-weight: bold;
  font-size: 14px;
  color: #333333;
}

.optimized-table :deep(.ant-table-tbody > tr > td) {
  white-space: nowrap; /* 表格内容不换行 */
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 12px 8px; /* 增加内边距 */
  font-family: PingFang SC, PingFang SC !important;
  font-weight: 400 !important;
  font-size: 14px !important;
  color: #333333 !important;
}

/* "值"列样式：一行展示30个字符，超出省略号 */
.config-value-text {
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* 操作列固定右侧样式 */
.optimized-table :deep(.ant-table-tbody > tr > td:last-child) {
  position: sticky;
  right: 0;
  background: white;
  z-index: 10;
  box-shadow: -2px 0 5px rgba(0, 0, 0, 0.05);
}

/* 选中行的操作列样式 */
.optimized-table :deep(.ant-table-tbody > tr.ant-table-row-selected > td:last-child) {
  position: sticky;
  right: 0;
  background: white !important;
  z-index: 10;
  box-shadow: -2px 0 5px rgba(0, 0, 0, 0.05);
}

.optimized-table :deep(.ant-table-tbody > tr.ant-table-row-selected:hover > td:last-child) {
  position: sticky;
  right: 0;
  background: #f5f5f5 !important;
  z-index: 10;
  box-shadow: -2px 0 5px rgba(0, 0, 0, 0.05);
}

.optimized-table :deep(.ant-table-thead > tr > th:last-child) {
  position: sticky;
  right: 0;
  background: #fafafa;
  z-index: 11;
  box-shadow: -2px 0 5px rgba(0, 0, 0, 0.05);
}

/* 行悬停效果 */
.optimized-table :deep(.ant-table-tbody > tr:hover > td) {
  background-color: #f5f5f5;
}

/* 去掉表格边框 */
:deep(.ant-table) {
  border: none !important;
}

:deep(.ant-table-container) {
  border: none !important;
}

:deep(.ant-table-thead > tr > th) {
  border-bottom: 1px solid #f0f0f0 !important;
  border-right: none !important;
}

:deep(.ant-table-thead > tr > th:first-child) {
  border-left: none !important;
}

:deep(.ant-table-tbody > tr > td) {
  border-bottom: 1px solid #f0f0f0 !important;
  border-right: none !important;
}

:deep(.ant-table-tbody > tr > td:first-child) {
  border-left: none !important;
}

:deep(.ant-table-tbody > tr:last-child > td) {
  border-bottom: none !important;
}

/* 表格滚动条样式优化 */
:deep(.ant-table-body) {
  overflow-x: auto !important;
  scrollbar-width: thin;
  scrollbar-color: #d9d9d9 #f5f5f5;
}

:deep(.ant-table-body::-webkit-scrollbar) {
  height: 8px;
}

:deep(.ant-table-body::-webkit-scrollbar-track) {
  background: #f5f5f5;
  border-radius: 4px;
}

:deep(.ant-table-body::-webkit-scrollbar-thumb) {
  background: #d9d9d9;
  border-radius: 4px;
}

:deep(.ant-table-body::-webkit-scrollbar-thumb:hover) {
  background: #bfbfbf;
}

/* 序号列样式优化 */
:deep(.ant-table-tbody > tr > td:first-child) {
  font-family: PingFang SC, PingFang SC !important;
  font-weight: 400 !important;
  font-size: 14px !important;
  color: #333333 !important;
}

/* 选中的行样式 - 去掉背景色 */
:deep(.ant-table-tbody > tr.ant-table-row-selected > td) {
  background-color: transparent !important;
}

:deep(.ant-table-tbody > tr.ant-table-row-selected:hover > td) {
  background-color: transparent !important;
}

/* 表格复选框样式 */
.optimized-table :deep(.ant-checkbox-inner) {
  width: 16px !important;
  height: 16px !important;
  border-radius: 0 !important;
  border-color: #999999 !important;
}

.optimized-table :deep(.ant-checkbox-checked .ant-checkbox-inner) {
  background-color: #165dff !important;
  border-color: #165dff !important;
}

.optimized-table :deep(.ant-checkbox-checked .ant-checkbox-inner::after) {
  border-color: #ffffff !important;
}

.optimized-table :deep(.ant-checkbox:hover .ant-checkbox-inner) {
  border-color: #165dff !important;
}

.optimized-table :deep(.ant-checkbox-input:focus + .ant-checkbox-inner) {
  border-color: #165dff !important;
}

.optimized-table :deep(.ant-checkbox-checked:hover .ant-checkbox-inner) {
  background-color: #165dff !important;
  border-color: #165dff !important;
}

/* 全选按钮（indeterminate状态）背景改为蓝色 */
.optimized-table :deep(.ant-checkbox-indeterminate .ant-checkbox-inner) {
  background-color: #165dff !important;
  border-color: #165dff !important;
}

.optimized-table :deep(.ant-checkbox-indeterminate .ant-checkbox-inner::after) {
  background-color: #ffffff !important;
  border-top-color: #ffffff !important;
  border-right-color: transparent !important;
  border-bottom-color: transparent !important;
  border-left-color: transparent !important;
}

.optimized-table :deep(.ant-checkbox-indeterminate:hover .ant-checkbox-inner) {
  background-color: #165dff !important;
  border-color: #165dff !important;
}

/* 确保全选按钮在选中状态时也是蓝色，覆盖可能的黑色背景 */
.optimized-table :deep(.ant-table-thead .ant-checkbox-checked .ant-checkbox-inner),
.optimized-table :deep(.ant-table-thead .ant-checkbox-indeterminate .ant-checkbox-inner) {
  background-color: #165dff !important;
  border-color: #165dff !important;
}

.optimized-table :deep(.ant-table-thead .ant-checkbox-checked .ant-checkbox-inner::after) {
  border-color: #ffffff !important;
}

/* 分组、变量名、变量标题、描述字段：一行展示完整内容，不换行，宽度自适应 */
.optimized-table :deep(.ant-table-tbody > tr > td.config-group-cell),
.optimized-table :deep(.ant-table-tbody > tr > td.config-name-cell),
.optimized-table :deep(.ant-table-tbody > tr > td.config-title-cell),
.optimized-table :deep(.ant-table-tbody > tr > td.config-description-cell) {
  overflow: visible !important;
  text-overflow: clip !important;
  white-space: nowrap !important;
}

/* Confirm 弹窗按钮样式 */
:deep(.ant-modal-confirm .ant-btn) {
  border-radius: 4px !important;
}

:deep(.ant-modal-confirm .ant-btn-default) {
  border-radius: 4px !important;
}

:deep(.ant-modal-confirm .ant-btn-primary) {
  background-color: #165dff !important;
  border-color: #165dff !important;
  border-radius: 4px !important;
  color: #ffffff !important;
}

:deep(.ant-modal-confirm .ant-btn-primary:hover:not(:disabled)) {
  background-color: #4080ff !important;
  border-color: #4080ff !important;
  color: #ffffff !important;
}

:deep(.ant-modal-confirm .ant-btn-primary:focus:not(:disabled)) {
  background-color: #165dff !important;
  border-color: #165dff !important;
  color: #ffffff !important;
}

:deep(.ant-modal-confirm .ant-btn-primary:active:not(:disabled)) {
  background-color: #165dff !important;
  border-color: #165dff !important;
  color: #ffffff !important;
}
</style>

<style>
/* Confirm 弹窗按钮样式 - 全局样式确保生效（confirm弹窗是动态创建的） */
.ant-modal-confirm .ant-modal-confirm-btns .ant-btn {
  border-radius: 4px !important;
}

.ant-modal-confirm .ant-modal-confirm-btns .ant-btn-default {
  border-radius: 4px !important;
}

.ant-modal-confirm .ant-modal-confirm-btns .ant-btn-primary {
  background-color: #165dff !important;
  border-color: #165dff !important;
  border-radius: 4px !important;
  color: #ffffff !important;
}

.ant-modal-confirm .ant-modal-confirm-btns .ant-btn-primary:hover:not(:disabled) {
  background-color: #4080ff !important;
  border-color: #4080ff !important;
  color: #ffffff !important;
}

.ant-modal-confirm .ant-modal-confirm-btns .ant-btn-primary:focus:not(:disabled) {
  background-color: #165dff !important;
  border-color: #165dff !important;
  color: #ffffff !important;
}

.ant-modal-confirm .ant-modal-confirm-btns .ant-btn-primary:active:not(:disabled) {
  background-color: #165dff !important;
  border-color: #165dff !important;
  color: #ffffff !important;
}
</style>
